// OWNER: FRONTEND-ADMIN. Pure CSV helpers (no DOM): RFC-4180 parse/serialise, import header preflight,
// splitting big files into ≤ 5,000-row requests (SPEC §9), and the rejected-rows download.
import type { ImportReport, ImportRowError } from '../../../src/shared/api';

export const MAX_IMPORT_ROWS = 5000;

/** RFC-4180 parser: quoted fields, "" escapes, embedded commas/newlines, CRLF/LF/CR, leading BOM. */
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let i = text.charCodeAt(0) === 0xfeff ? 1 : 0;
  let quoted = false;
  let fieldStarted = false;
  const endField = () => { row.push(field); field = ''; fieldStarted = false; };
  const endRow = () => { endField(); rows.push(row); row = []; };
  for (; i < text.length; i++) {
    const ch = text[i];
    if (quoted) {
      if (ch === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; } else quoted = false;
      } else field += ch;
      continue;
    }
    if (ch === '"' && !fieldStarted) { quoted = true; fieldStarted = true; continue; }
    if (ch === ',') { endField(); continue; }
    if (ch === '\r' || ch === '\n') {
      if (ch === '\r' && text[i + 1] === '\n') i++;
      endRow();
      continue;
    }
    field += ch;
    fieldStarted = true;
  }
  if (quoted) throw new Error('The CSV has an unclosed quote, so the rows after it can’t be read. Fix the file and try again.');
  if (field !== '' || fieldStarted || row.length > 0) endRow();
  // drop blank lines (a lone empty field)
  return rows.filter((r) => !(r.length === 1 && r[0].trim() === ''));
}

function quoteField(v: string): string {
  return /[",\r\n]/.test(v) || /^\s|\s$/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
}

/** A value a spreadsheet reads as a plain number or phone ('+1 (416) 555-1234', '-3.5'). It can't hold a formula,
 *  so the guard leaves it intact. Same pattern as CORE's src/worker/lib/csv.ts. */
const PLAIN_NUMBER_RE = /^\+?[\d\s().-]+$/;

/** Spreadsheet formula-injection guard (OWASP), matching CORE's csvCell(v, true): text starting with = + - @ tab or CR
 *  gets a leading apostrophe unless the whole value is a plain number or phone. */
export function guardFormula(v: string): string {
  return /^[=+\-@\t\r]/.test(v) && !PLAIN_NUMBER_RE.test(v) ? `'${v}` : v;
}

/** Serialise rows as RFC-4180 CSV with CRLF line endings. `guardFormulas` for every file a person downloads and opens
 *  in Excel/Sheets (text cells only; numbers are never guarded). Import chunks sent to the server stay unguarded. */
export function toCsv(rows: Array<Array<string | number | null | undefined>>, opts: { guardFormulas?: boolean } = {}): string {
  const cell = (v: string | number | null | undefined): string => {
    if (v === null || v === undefined) return '';
    return quoteField(typeof v === 'string' && opts.guardFormulas ? guardFormula(v) : String(v));
  };
  return rows.map((r) => r.map(cell).join(',')).join('\r\n') + '\r\n';
}

// ---------- import header preflight (mirrors SPEC §9 synonyms; the server's report is the source of truth) ----------

export interface ImportColumn { field: string; synonyms: string[]; required: boolean; label: string }

export const IMPORT_COLUMNS: ImportColumn[] = [
  { field: 'name', synonyms: ['name', 'business', 'business_name', 'company'], required: true, label: 'Business name' },
  { field: 'phone', synonyms: ['phone', 'phone_number', 'main_phone', 'telephone'], required: true, label: 'Phone' },
  { field: 'phone_e164', synonyms: ['phone_e164'], required: false, label: 'Phone (E.164)' },
  { field: 'ext', synonyms: ['ext', 'extension'], required: false, label: 'Extension' },
  { field: 'country', synonyms: ['country'], required: false, label: 'Country' },
  { field: 'state', synonyms: ['state', 'province', 'state_prov', 'region'], required: true, label: 'State / province' },
  { field: 'city', synonyms: ['city'], required: false, label: 'City' },
  { field: 'postal', synonyms: ['postal', 'zip', 'postal_code'], required: false, label: 'Postal / ZIP' },
  { field: 'website', synonyms: ['website', 'url', 'site'], required: false, label: 'Website' },
  { field: 'gbp_url', synonyms: ['gbp_url', 'google_url', 'maps_url', 'gbp'], required: false, label: 'Google profile' },
  { field: 'email', synonyms: ['email'], required: false, label: 'Email' },
  { field: 'email_source_url', synonyms: ['email_source_url'], required: false, label: 'Email source URL' },
  { field: 'dm_name', synonyms: ['dm_name', 'owner', 'owner_name', 'contact', 'contact_name'], required: false, label: 'Decision-maker' },
  { field: 'dm_role', synonyms: ['dm_role', 'title', 'role'], required: false, label: 'DM role' },
  { field: 'dm_source_url', synonyms: ['dm_source_url'], required: false, label: 'DM source URL' },
  { field: 'source_url', synonyms: ['source_url', 'source'], required: true, label: 'Source URL' },
  { field: 'niche', synonyms: ['niche', 'category', 'type'], required: false, label: 'Niche' },
  { field: 'observation', synonyms: ['observation'], required: false, label: 'Observation' },
  { field: 'observation_source_url', synonyms: ['observation_source_url'], required: false, label: 'Observation source' },
];

export function normalizeHeader(h: string): string {
  return h.trim().toLowerCase().replace(/[\s-]+/g, '_');
}

export interface HeaderMap {
  mapped: Array<{ field: string; label: string; column: string }>;
  unknown: string[];                 // columns the importer will ignore
  missingRequired: string[];         // labels of required fields with no column
}

export function mapImportHeader(header: string[]): HeaderMap {
  const norm = header.map(normalizeHeader);
  const used = new Set<number>();
  const mapped: HeaderMap['mapped'] = [];
  const missingRequired: string[] = [];
  for (const col of IMPORT_COLUMNS) {
    const idx = norm.findIndex((h, i) => !used.has(i) && col.synonyms.includes(h));
    if (idx >= 0) { used.add(idx); mapped.push({ field: col.field, label: col.label, column: header[idx] }); }
    else if (col.required) missingRequired.push(col.label);
  }
  // either phone column satisfies "Phone"
  if (mapped.some((m) => m.field === 'phone_e164')) {
    const i = missingRequired.indexOf('Phone');
    if (i >= 0) missingRequired.splice(i, 1);
  }
  const unknown = header.filter((_, i) => !used.has(i) && norm[i] !== '');
  return { mapped, unknown, missingRequired };
}

export interface CsvPlan { header: string[]; dataRows: number; chunks: string[]; chunkSize: number }

/** Parse once, then re-serialise into chunks of ≤ maxRows data rows, each with the header. */
export function planImport(text: string, maxRows = MAX_IMPORT_ROWS): CsvPlan {
  const rows = parseCsv(text);
  if (rows.length === 0) throw new Error('The file is empty.');
  const [header, ...data] = rows;
  if (data.length === 0) throw new Error('The file has a header row but no data rows.');
  const chunks: string[] = [];
  for (let i = 0; i < data.length; i += maxRows) chunks.push(toCsv([header, ...data.slice(i, i + maxRows)]));
  return { header, dataRows: data.length, chunks, chunkSize: maxRows };
}

/** Merge per-chunk reports; row numbers of later chunks are shifted by the rows that came before them. */
export function mergeImportReports(reports: ImportReport[], chunkSize: number): ImportReport & { importIds: number[] } {
  const merged: ImportReport & { importIds: number[] } = {
    importId: reports[0]?.importId ?? 0, importIds: [], total: 0, imported: 0, reusedBusinesses: 0,
    rejected: [], flagged: [], lookupsQueued: 0,
  };
  reports.forEach((r, k) => {
    const offset = k * chunkSize;
    merged.importIds.push(r.importId);
    merged.total += r.total;
    merged.imported += r.imported;
    merged.reusedBusinesses += r.reusedBusinesses;
    merged.lookupsQueued += r.lookupsQueued;
    for (const e of r.rejected ?? []) merged.rejected.push({ ...e, row: e.row + offset });
    for (const f of r.flagged ?? []) merged.flagged.push({ ...f, row: f.row + offset });
  });
  return merged;
}

/** CSV of rejected rows: row, reason, then every original column (first-seen order) so it can be fixed and re-imported.
 *  Formula-guarded: the cells are whatever the source file had, and this file gets opened in a spreadsheet. */
export function rejectedRowsCsv(rejected: ImportRowError[]): string {
  const cols: string[] = [];
  const seen = new Set<string>();
  for (const r of rejected) for (const k of Object.keys(r.raw ?? {})) if (!seen.has(k)) { seen.add(k); cols.push(k); }
  const out: Array<Array<string | number>> = [['row', 'reject_reason', ...cols]];
  for (const r of rejected) out.push([r.row, r.reason, ...cols.map((c) => r.raw?.[c] ?? '')]);
  return toCsv(out, { guardFormulas: true });
}
